import Vue from 'vue';
import { AppModule } from '@/store/modules/app';
import { ProfessionModule } from '@/store/modules/profession';
import { ClustersModule } from '@/store/modules/clusters';
import { JobType } from '@/types/ProfessionType';
import { CourseModalModule } from '@/store/modules/courses/courseModal';
import { NotebookModule } from '@/store/modules/notebook';

// The course search uses the job search state, as before: ProfessionModule (occupations, place,
// radius, full text, pagination) and AppModule.visibleJobs = a started search. An occupational
// field (ClustersModule cluster_id) is a list of its own (/portal/cluster/courselist) shown while
// AppModule.visibleClusterJobs is on; in the catalog the chosen field narrows the occupations.

// One course of a result list, the same for both lists (their fields are named differently)
export interface CourseRow {
  id: number
  title: string
  provider: string
  location: string
  startDate: string
  url: string // job server page of the course (showkurs)
  showInNewTab: boolean // provider pages that refuse to be embedded
  avgsType?: number // funded with an education voucher (Bildungsgutschein)
}

// The course lists name their fields differently: the search (/course/list), a field
// (/portal/cluster/courselist) and the notebook kept in the browser
export const toCourseRow = (course: any): CourseRow => ({
  id: course.id,
  title: course.course_name ?? course.name ?? '',
  provider: course.course_provider ?? course.provider ?? course.position ?? '',
  location: course.location ?? '',
  startDate: course.start_date ?? course.lastdate ?? '',
  url: course.url,
  showInNewTab: Boolean(course.show_in_new_tab),
  avgsType: course.avgs_type,
});

// Opens a course in the course panel (TsCoursePanel) with its list for ‹ ›; provider pages
// that cannot be embedded open in a new tab, as before
export const openCourse = async(root: Vue, course: any, list: any[] = [course]): Promise<void> => {
  if (course.show_in_new_tab) {
    window.open(course.url, '_blank', 'noopener');
    return;
  }
  const index = Math.max(0, list.findIndex((item) => item.id === course.id));
  CourseModalModule.UPDATE_CURRENT_COURSES_LIST(list);
  CourseModalModule.UPDATE_CURRENT_COURSE(course);
  await CourseModalModule.UPDATE_CURRENT_COURSE_INDEX(index);
  root.$emit('bv::toggle::collapse', 'course-sidebar');
};

export interface CourseField {
  id: number
  name: string
  count: number
  picture?: string
}

// Occupational fields of the portal (PAT) with their course counts
export const courseFields = (): CourseField[] => {
  const counts: { id: number, count: number }[] = AppModule.courseCountPortal.clusters || [];
  return (AppModule.settingPortal.clusters || []).map((cluster) => ({
    ...cluster,
    count: counts.find((item) => item.id === cluster.id)?.count || 0,
  }));
};

export const isClusterCourseList = (): boolean => AppModule.visibleClusterJobs && !AppModule.visibleJobs;

export const courseSearchStarted = (): boolean => AppModule.visibleJobs || AppModule.visibleClusterJobs;

// The courses of one occupational field (CoursesView then opens the results page)
export const searchCourseField = (field: { id: number, name: string }): void => {
  ProfessionModule.SetParamsDefault();
  ProfessionModule.SetJobType(JobType.search);
  ClustersModule.SetClusterParams({ obj: 'cluster_id', value: field.id });
  ClustersModule.SetProfessionTitle(field.name);
  ClustersModule.SetPagination({ obj: 'page', value: 1 });
  AppModule.SetVisibleJobs(false);
  AppModule.SetVisibleClusterJobs(true);
};

// The search card / catalog: a course search from the chosen occupations, place and text
export const startCourseSearch = (fulltext = false): void => {
  ProfessionModule.SET_FILTER_DEFAULT();
  ProfessionModule.SET_SEARCH_DEFAULT(fulltext);
  ProfessionModule.SET_PARAMS_DEFAULT();
  ProfessionModule.SET_TAGS_DEFAULT();
  ProfessionModule.SetProfessionTitle('');
  ProfessionModule.SetJobType(JobType.search);
  // (SetPagination also renews the link of the search, the mutations above do not)
  ProfessionModule.SetPagination({ obj: 'page', value: 1 });
  AppModule.SetVisibleMap(false);
  AppModule.SetVisibleClusterJobs(false);
  AppModule.SetVisibleJobs(true);
};

// "Kurssuche" from the tab bar: a clean search
export const resetCourseSearch = (): void => {
  ProfessionModule.SetParamsDefault();
  ProfessionModule.SetTagsDefault();
  AppModule.SetVisibleJobs(false);
  AppModule.SetVisibleMap(false);
  AppModule.SetVisibleClusterJobs(false);
  ClustersModule.ResetClusterData();
};

// "Kurskatalog" from the tab bar: no list, a chosen field keeps narrowing the catalog
export const openCourseCatalog = (): void => {
  ProfessionModule.SetParamsDefault();
  ProfessionModule.SetTagsDefault();
  AppModule.SetVisibleJobs(false);
  AppModule.SetVisibleMap(false);
  AppModule.SetVisibleClusterJobs(false);
};

export const selectedCourseField = (): CourseField | null => {
  const id = Number(ClustersModule.jobClusterParams.cluster_id || 0);
  return courseFields().find((field) => field.id === id) || null;
};

// ─── notebook ────────────────────────────────────────────────────────────────

export const isCourseInNotebook = (id: number): boolean => NotebookModule.notebookCourses.some((item: { id: number }) => item.id === id);

// Courses are kept in the browser notebook. (The old code sent them to POST /notepad as jobs when
// logged in, but the API knows no course type there: they landed among the jobs.)
export const toggleCourseNotepad = (course: CourseRow): void => {
  NotebookModule.setCourseNotepadItemLocal({
    isMarked: isCourseInNotebook(course.id),
    id: course.id,
    position: course.provider,
    location: course.location,
    lastdate: course.startDate,
    course_name: course.title,
    url: course.url,
    show_in_new_tab: course.showInNewTab,
  });
};
